import { Loader } from '../../components/Loader';
import { AddressDisplay } from '../../components/address-display';
import {
  Button,
  ButtonVariants,
  LinkButton,
  Typography,
  Modal,
} from '../../components/primitives';
import Alert, { AlertProps } from '../../components/ui/Alert';
import { Icons } from '../../icons';
import { testId } from '../../utils';
import { Separator } from '@radix-ui/react-separator';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type AddressItemProps = {
  'data-testid': string;
  address: Cornerstone.ValueAddress;
  refetch: () => void;
  setError: (error: unknown) => void;
};

const AddressItem: React.FC<AddressItemProps> = ({
  address,
  refetch,
  setError,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.user' });
  const navigate = useNavigate();

  const updateAddressResponse = Cornerstone.useUpdateAddress({
    mutation: {
      onSuccess: () => {
        refetch();
      },
      onError: (error: any) => {
        setError(error);
        console.error({
          title: 'Failed to set default address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const onSetDefault = React.useCallback(() => {
    updateAddressResponse.mutate({
      addressId: address.id!,
      data: {
        ...address,
        defaultShipping: true,
      },
    });
  }, [address, updateAddressResponse]);

  const deleteAddressMutation = Cornerstone.useDeleteAddress({
    mutation: {
      onSuccess: () => {
        refetch();
      },
      onError: (error: any) => {
        setError(error);
        console.error({
          title: 'Failed to delete address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const deleteAddress = React.useCallback(() => {
    if (address.id !== undefined) {
      deleteAddressMutation.mutate({ addressId: address.id });
    }
  }, [address.id, deleteAddressMutation]);

  const isLoading =
    deleteAddressMutation.isLoading || updateAddressResponse.isLoading;

  const submitElement: React.ReactNode = (
    <Button
      {...testId(dataTestId, 'address-item', 'delete-modal', 'ok')}
      key="OK"
      onClick={deleteAddress}
    >
      {t('settings.account.OK_button')}
    </Button>
  );
  const closeElement: React.ReactNode = (
    <Button
      {...testId(dataTestId, 'address-item', 'delete-modal', 'cancel')}
      key="Cancel"
      variant={ButtonVariants.Secondary}
    >
      {t('settings.account.cancel_button')}
    </Button>
  );

  const editAddress = () => {
    navigate(`${address.id}`);
  };

  const [open, setOpen] = useState(false);

  return (
    <div
      className="flex flex-col gap-4"
      {...testId(dataTestId, 'address-item')}
    >
      <AddressDisplay {...testId('address-display')} address={address} />
      <div className="flex md:flex-col gap-4 md:gap-0">
        <div className="flex gap-4">
          <LinkButton
            {...testId(dataTestId, 'address-item', 'edit')}
            disabled={isLoading}
            onClick={editAddress}
          >
            {t('settings.account.edit_button')}
          </LinkButton>

          <Modal
            {...testId(dataTestId, 'address-item', 'delete-modal')}
            triggerElement={
              <LinkButton
                {...testId(dataTestId, 'address-item', 'delete')}
                disabled={isLoading}
              >
                {t('addresses.delete')}
              </LinkButton>
            }
            icon={<Icons.WarningIcon />}
            title={t('addresses.delete_title')}
            contentElement={
              <div className="space-y-6">
                <Typography variant="body">
                  {t('addresses.delete_warning')}
                </Typography>
                <div>
                  <Typography variant="body">
                    {t('addresses.delete_confirm')}
                  </Typography>
                </div>
              </div>
            }
            closeElement={closeElement}
            submitElement={submitElement}
            open={open}
            setOpen={setOpen}
          />
        </div>
        {!address.defaultShipping && (
          <LinkButton
            {...testId(dataTestId, 'address-item', 'set-default')}
            onClick={onSetDefault}
            disabled={isLoading}
          >
            {t('addresses.set_default_short')}
          </LinkButton>
        )}
      </div>
    </div>
  );
};

export const AddressBook: React.FC = () => {
  const [error, setError] = React.useState<unknown>();
  const { t } = useTranslation(undefined, { keyPrefix: 'route.user' });
  const navigate = useNavigate();

  const userAddressesQuery = Cornerstone.useGetUserAddresses();

  const onClick = React.useCallback(() => {
    navigate('add', {
      relative: 'route',
    });
  }, [navigate]);

  const alertProps = useAlertProps(error || userAddressesQuery.isError);
  const { data: addressesResponse, isLoading, refetch } = userAddressesQuery;
  const { defaultShipping, additional } = addressesResponse?.data || {};

  return (
    <div className="flex flex-col gap-6 md:gap-12 w-full">
      <Typography variant="title-default" className="md:text-left text-center">
        {t('navigation.address_book')}
      </Typography>
      {alertProps && <Alert {...alertProps} />}

      {isLoading && <Loader className="text-brand" variant="bars" size="lg" />}
      {defaultShipping && (
        <>
          <div className="flex flex-col gap-4">
            <Typography variant="title-small" className="text-gray-soft">
              {t('addresses.default_address')}
            </Typography>
            {!isLoading && (
              <AddressItem
                data-testid="default-address"
                address={defaultShipping}
                refetch={refetch}
                setError={setError}
              />
            )}
          </div>
          <Separator className="w-full h-[1px] bg-gray-light" />
        </>
      )}

      <Button
        {...testId('add-address')}
        className="self-start px-8 md:w-auto w-full"
        size="sm"
        onClick={onClick}
      >
        {t('addresses.add_address')}
      </Button>
      <div className="flex flex-col gap-4">
        {isLoading && <Loader variant="bars" size="lg" />}
        {additional && additional.length > 0 && (
          <Typography variant="title-small" className="text-gray-soft">
            {t('addresses.additional_addresses')}
          </Typography>
        )}
        {!isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-16">
            {additional?.map((address) => (
              <AddressItem
                data-testid="additional-addresses"
                key={address.id}
                address={address}
                refetch={refetch}
                setError={setError}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function useAlertProps(error: unknown): AlertProps | null {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.auth' });
  if (!error) {
    return null;
  }

  return {
    ...testId('alert'),
    variant: 'error',
    children: t('generic_error'),
  };
}
